import { forwardRef, useState, type JSX } from 'react';

import { Input } from '../../primitives/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
} from '../../primitives/input-group';
import { LoadingAddon } from '../../primitives/loading-addon';
import { cn } from '../../utils/cn';

export interface InputFieldProps extends React.ComponentProps<typeof Input> {
  button?: {
    disabled?: boolean;
    icon?: JSX.Element;
    id?: string;
    onClick: VoidFunction;
    text?: string;
  };
  counter?: {
    enable: boolean;
  };
  isLoading?: boolean;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      button,
      disabled = false,
      counter,
      className,
      sizesx,
      isLoading,
      value: tmpValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const value = tmpValue === undefined ? internalValue : tmpValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalValue(e.target.value);
      }
    };

    const currentLength = typeof value === 'string' ? value.length : 0;
    const hasCounter = Boolean(counter?.enable);
    const { icon, id, onClick: buttonClick, text } = button ?? {};
    const hasButton = typeof buttonClick === 'function';

    return (
      <InputGroup disabled={disabled || isLoading}>
        <Input
          autoComplete="off"
          className={cn(
            'border-0 shadow-none ring-0 focus-within:ring-0 focus-visible:ring-0',
            className,
          )}
          data-slot="input-group-control"
          disabled={disabled || isLoading}
          ref={ref}
          sizesx={sizesx}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {hasCounter || hasButton ? (
          <InputGroupAddon align="block-end" className="flex-wrap justify-end gap-4">
            {hasCounter ? (
              <InputGroupText>
                {currentLength} {currentLength === 1 ? 'carácter' : 'caracteres'}
              </InputGroupText>
            ) : null}
            {hasButton ? (
              <InputGroupButton
                className="flex-none"
                disabled={disabled || button?.disabled || isLoading}
                id={id}
                size="xs"
                title={text ?? 'Acción'}
                variant="secondary"
                onClick={buttonClick}
              >
                {icon}
                {text}
              </InputGroupButton>
            ) : null}
          </InputGroupAddon>
        ) : null}
        {isLoading && <LoadingAddon />}
      </InputGroup>
    );
  },
);

InputField.displayName = 'InputField';

export { InputField };
